import React from 'react';
import DatePicker from 'react-datepicker';
import { X } from 'lucide-react';
import 'react-datepicker/dist/react-datepicker.css';
import './DateField.css';

/** Parse a stored date/timestamp string into a local Date (or null). */
export function parseDateValue(raw) {
  const s = String(raw || '').trim();
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(s);
  if (m) {
    return new Date(
      Number(m[1]),
      Number(m[2]) - 1,
      Number(m[3]),
      Number(m[4] || 0),
      Number(m[5] || 0),
      Number(m[6] || 0)
    );
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Format a Date as YYYY-MM-DD or YYYY-MM-DD HH:mm:ss. */
export function formatDateValue(date, family = 'date') {
  if (!date || Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  if (family === 'date') return `${y}-${mo}-${day}`;
  const h = String(date.getHours()).padStart(2, '0');
  const mi = String(date.getMinutes()).padStart(2, '0');
  const sec = String(date.getSeconds()).padStart(2, '0');
  return `${y}-${mo}-${day} ${h}:${mi}:${sec}`;
}

const YEAR_MIN = new Date(1970, 0, 1);
const yearMax = () => new Date(new Date().getFullYear() + 5, 11, 31);

const INPUT_CLASS =
  'flex h-10 w-full rounded-lg border border-input bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#18181B]/10 focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50';

/**
 * Typeable date input with a calendar that has month and year dropdowns.
 * @param {'date' | 'timestamp'} family
 */
const DateField = ({
  value,
  onChange,
  family = 'date',
  className = '',
  placeholder,
  disabled = false,
  id,
  required = false,
  isClearable = true,
  minDate,
  maxDate,
}) => {
  const isTimestamp = family === 'timestamp';
  const selected = parseDateValue(value);
  const showClear = isClearable && !disabled && Boolean(selected);

  return (
    <div className="date-field-wrap">
      <DatePicker
        id={id}
        selected={selected}
        onChange={(date) => onChange(date ? formatDateValue(date, family) : '')}
        disabled={disabled}
        required={required}
        showTimeSelect={isTimestamp}
        timeIntervals={15}
        timeCaption="Time"
        dateFormat={isTimestamp ? 'yyyy-MM-dd HH:mm:ss' : 'yyyy-MM-dd'}
        placeholderText={placeholder || (isTimestamp ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD')}
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        minDate={minDate || YEAR_MIN}
        maxDate={maxDate || yearMax()}
        strictParsing
        enableTabLoop={false}
        className={`${INPUT_CLASS} ${className}`}
        wrapperClassName="date-field-inner"
        calendarClassName="date-field-calendar"
        popperClassName="date-field-popper"
        popperPlacement="bottom-start"
      />
      {showClear ? (
        <button
          type="button"
          className="date-field-clear"
          aria-label="Clear date"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onChange('')}
        >
          <X className="h-2.5 w-2.5" strokeWidth={2.5} aria-hidden />
        </button>
      ) : null}
    </div>
  );
};

export default DateField;
